import { ID, PaginatedResult } from '../../domain/types/common';
import { Prompt } from '../../domain/models/prompt';
import { StoragePrompt } from './models/storageTypes';
import { PromptMapper } from './mappers/promptMapper';
import { IPromptRepository, PromptSearchOptions } from '../../domain/repositories/promptRepository';
import { ChromeStorageRepository } from './chromeStorageRepository';
import { DomainError } from '../../domain/errors/domainError';
import { ErrorCode } from '../../domain/errors/errorCodes';

/**
 * Validate a parsed import candidate has the minimum shape of a StoragePrompt.
 * Returns an error string if invalid, or null when the item is acceptable.
 */
function validateImportedPrompt(candidate: unknown): string | null {
  if (typeof candidate !== 'object' || candidate === null) {
    return 'Prompt is not an object';
  }
  const p = candidate as Record<string, unknown>;
  if (typeof p.id !== 'string' || p.id.trim() === '') {
    return 'Prompt is missing a valid string id';
  }
  if (typeof p.title !== 'string') {
    return 'Prompt is missing a valid title';
  }
  if (typeof p.text !== 'string') {
    return 'Prompt is missing valid text';
  }
  if (!Array.isArray(p.tags)) {
    return 'Prompt tags must be an array';
  }
  return null;
}

export class ChromePromptRepository
  extends ChromeStorageRepository<Prompt, StoragePrompt>
  implements IPromptRepository
{
  protected toDomain(storage: StoragePrompt): Prompt {
    return PromptMapper.toDomain(storage);
  }

  protected toStorage(domain: Prompt): StoragePrompt {
    return PromptMapper.toStorage(domain);
  }

  protected toDomainList(storageList: StoragePrompt[]): Prompt[] {
    return PromptMapper.toDomainList(storageList);
  }

  protected toStorageList(domainList: Prompt[]): StoragePrompt[] {
    return PromptMapper.toStorageList(domainList);
  }
  constructor() {
    super('prompts', 'Prompt');
  }

  async findByFolder(
    folderId: ID,
    options?: Omit<PromptSearchOptions, 'folderId'>
  ): Promise<PaginatedResult<Prompt>> {
    const items = await super.getStorageData();
    let filtered = this.filterItems(items, item => item.folderId === folderId);

    if (options?.textSearch) {
      const searchLower = options.textSearch.toLowerCase();
      filtered = this.filterItems(
        filtered,
        item =>
          item.title.toLowerCase().includes(searchLower) ||
          item.text.toLowerCase().includes(searchLower) ||
          item.description.toLowerCase().includes(searchLower) ||
          item.tags.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    if (options?.tags?.length) {
      filtered = this.filterItems(filtered, item =>
        options.tags!.every((tag: string) => item.tags.includes(tag))
      );
    }

    if (options?.fromDate) {
      filtered = this.filterItems(filtered, item => item.updatedAt >= options.fromDate!);
    }

    if (options?.toDate) {
      filtered = this.filterItems(filtered, item => item.updatedAt <= options.toDate!);
    }

    if (options?.sortBy && typeof options.sortBy === 'string') {
      filtered = this.sortItems(
        filtered,
        options.sortBy as keyof StoragePrompt,
        options.sortDirection
      );
    }

    return this.paginateResults(filtered, options?.page, options?.pageSize);
  }

  async findByTags(
    tags: string[],
    options?: Omit<PromptSearchOptions, 'tags'>
  ): Promise<PaginatedResult<Prompt>> {
    const items = await super.getStorageData();
    let filtered = this.filterItems(items, item =>
      tags.every((tag: string) => item.tags.includes(tag))
    );

    if (options?.folderId) {
      filtered = this.filterItems(filtered, item => item.folderId === options.folderId);
    }

    if (options?.textSearch) {
      const searchLower = options.textSearch.toLowerCase();
      filtered = this.filterItems(
        filtered,
        item =>
          item.title.toLowerCase().includes(searchLower) ||
          item.text.toLowerCase().includes(searchLower) ||
          item.description.toLowerCase().includes(searchLower)
      );
    }

    if (options?.sortBy && typeof options.sortBy === 'string') {
      filtered = this.sortItems(
        filtered,
        options.sortBy as keyof StoragePrompt,
        options.sortDirection
      );
    }

    return this.paginateResults(filtered, options?.page, options?.pageSize);
  }

  async search(options: PromptSearchOptions): Promise<PaginatedResult<Prompt>> {
    const items = await super.getStorageData();
    let filtered = [...items];

    if (options.folderId) {
      filtered = this.filterItems(filtered, item => item.folderId === options.folderId);
    }

    if (options.tags?.length) {
      filtered = this.filterItems(filtered, item =>
        options.tags!.every(tag => item.tags.includes(tag))
      );
    }

    if (options.textSearch) {
      const searchLower = options.textSearch.toLowerCase();
      filtered = this.filterItems(
        filtered,
        item =>
          item.title.toLowerCase().includes(searchLower) ||
          item.text.toLowerCase().includes(searchLower) ||
          item.description.toLowerCase().includes(searchLower) ||
          item.tags.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    if (options.fromDate) {
      filtered = this.filterItems(filtered, item => item.updatedAt >= options.fromDate!);
    }

    if (options.toDate) {
      filtered = this.filterItems(filtered, item => item.updatedAt <= options.toDate!);
    }

    if (options.sortBy && typeof options.sortBy === 'string') {
      filtered = this.sortItems(
        filtered,
        options.sortBy as keyof StoragePrompt,
        options.sortDirection
      );
    }

    return this.paginateResults(filtered, options.page, options.pageSize);
  }

  async countByFolder(folderId: ID): Promise<number> {
    const items = await super.getStorageData();
    return items.filter(item => item.folderId === folderId).length;
  }

  async moveToFolder(promptId: ID, targetFolderId: ID): Promise<Prompt> {
    const prompt = await this.findById(promptId);
    if (!prompt) {
      throw new DomainError(ErrorCode.PROMPT_NOT_FOUND, `Prompt ${promptId} not found`);
    }
    const updatedPrompt = prompt.moveToFolder(targetFolderId);
    return this.update(promptId, updatedPrompt);
  }

  async addTags(promptId: ID, tags: string[]): Promise<Prompt> {
    const prompt = await this.findById(promptId);
    if (!prompt) {
      throw new DomainError(ErrorCode.PROMPT_NOT_FOUND, `Prompt ${promptId} not found`);
    }
    const updatedPrompt = tags.reduce((p, tag) => p.addTag(tag), prompt);
    return this.update(promptId, updatedPrompt);
  }

  async removeTags(promptId: ID, tags: string[]): Promise<Prompt> {
    const prompt = await this.findById(promptId);
    if (!prompt) {
      throw new DomainError(ErrorCode.PROMPT_NOT_FOUND, `Prompt ${promptId} not found`);
    }
    const updatedPrompt = tags.reduce((p, tag) => p.removeTag(tag), prompt);
    return this.update(promptId, updatedPrompt);
  }

  async findRecentlyUsed(limit: number = 10): Promise<Prompt[]> {
    const items = await super.getStorageData();
    const sortedItems = this.sortItems(items, 'updatedAt', 'desc').slice(0, limit);
    return PromptMapper.toDomainList(sortedItems);
  }

  async findFavorites(): Promise<Prompt[]> {
    const items = await super.getStorageData();
    const favoriteItems = items.filter(item => item.tags.includes('favorite'));
    return PromptMapper.toDomainList(favoriteItems);
  }

  async markAsUsed(promptId: ID): Promise<void> {
    const prompt = await this.findById(promptId);
    if (!prompt) {
      throw new DomainError(ErrorCode.PROMPT_NOT_FOUND, `Prompt ${promptId} not found`);
    }
    await this.update(promptId, { updatedAt: Date.now() });
  }

  async toggleFavorite(promptId: ID): Promise<void> {
    const prompt = await this.findById(promptId);
    if (!prompt) {
      throw new DomainError(ErrorCode.PROMPT_NOT_FOUND, `Prompt ${promptId} not found`);
    }
    const hasFavorite = prompt.tags.includes('favorite');
    const updatedPrompt = hasFavorite ? prompt.removeTag('favorite') : prompt.addTag('favorite');
    await this.update(promptId, updatedPrompt);
  }

  async bulkDelete(promptIds: ID[]): Promise<void> {
    return this.withStorageLock(async () => {
      const items = await super.getStorageData();
      const remainingItems = items.filter(item => !promptIds.includes(item.id));
      await super.setStorageData(remainingItems);
    });
  }

  async bulkMove(promptIds: ID[], targetFolderId: ID): Promise<void> {
    return this.withStorageLock(async () => {
      const items = await super.getStorageData();
      const updatedItems = items.map(item =>
        promptIds.includes(item.id) ? { ...item, folderId: targetFolderId } : item
      );
      await super.setStorageData(updatedItems);
    });
  }

  async exportPrompts(promptIds?: ID[]): Promise<string> {
    const items = await super.getStorageData();
    const prompts = promptIds ? items.filter(item => promptIds.includes(item.id)) : items;
    return JSON.stringify(prompts, null, 2);
  }

  async importPrompts(data: string): Promise<{
    imported: number;
    skipped: number;
    errors: Array<{ id: string; error: string }>;
  }> {
    try {
      const parsed = JSON.parse(data);
      if (!Array.isArray(parsed)) {
        throw new Error('Import data must be a JSON array of prompts');
      }
      const importedPrompts = parsed as unknown[];
      // Serialize the read-modify-write so a concurrent create/update/delete on
      // the prompts key can't clobber the import (or vice versa).
      return await this.withStorageLock(async () => {
        const existingItems = await super.getStorageData();
        const result = {
          imported: 0,
          skipped: 0,
          errors: [] as Array<{ id: string; error: string }>,
        };

        for (const candidate of importedPrompts) {
          // Validate each item BEFORE persisting so a malformed or foreign object
          // can't corrupt the whole prompts array.
          const validationError = validateImportedPrompt(candidate);
          if (validationError) {
            result.errors.push({
              id:
                candidate &&
                typeof candidate === 'object' &&
                typeof (candidate as { id?: unknown }).id === 'string'
                  ? (candidate as { id: string }).id
                  : 'unknown',
              error: validationError,
            });
            continue;
          }

          const prompt = candidate as StoragePrompt;

          // A duplicate id is a skip, not an error — keep the two counts distinct.
          if (existingItems.some(item => item.id === prompt.id)) {
            result.skipped++;
            continue;
          }

          existingItems.push(prompt);
          result.imported++;
        }

        await this.setStorageData(existingItems);
        return result;
      });
    } catch (error) {
      throw new DomainError(ErrorCode.VALIDATION_ERROR, 'Invalid import data format', error);
    }
  }
}
